/**
 * Pricing — three-tier pricing page with monthly/annual toggle.
 *
 * Pricing strategy (Feb 2026):
 *   • Flat monthly tiers: $60 / $150 / $250 CAD.
 *   • 17% discount on annual prepay (≈2 months free).
 *   • $49 paid onboarding, refunded against first month if they stay 30 days.
 *
 * SEO: JSON-LD Product/Offer for each tier so Google rich-cards work.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Sparkles, Shield, Zap, MapPin, Calculator, Loader2 } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import PublicBackButton from "@/components/PublicBackButton";
import EEATTrustBar from "@/components/EEATTrustBar";
import SmartChatBubble from "@/components/SmartChatBubble";
import LiveSignupCounter from "@/components/LiveSignupCounter";
import { setAudience, AUDIENCES } from "@/lib/personalizer";
import { api, getToken, openCheckoutSync, resolveCheckout, formatError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Kick off a Stripe checkout session for a paid plan and redirect the
 * visitor to the Stripe-hosted payment page. If the user isn't logged
 * in yet we fall back to the signup form with the picked plan encoded
 * — signup will replay the checkout after account creation.
 *
 * NOTE: This uses the shared `api` axios instance which already reads
 * the correct auth key (`crc_session_token`) from localStorage. A prior
 * revision hard-coded a non-existent `cr_token` key, so EVERY logged-in
 * click short-circuited to /signup and users looped back to the plan
 * grid — the exact bug reported by users Feb 2026.
 */

// Frontend uses friendly slugs (growth, portfolio) for URLs, but the
// backend `SUBSCRIPTION_PLANS` dict is keyed on (basic, starter,
// professional, enterprise). Map before we POST so the checkout doesn't
// 400 with "Unknown plan".
const PLAN_ALIAS = {
  diy: "diy",
  growth: "professional",
  portfolio: "enterprise",
};

async function goToStripeCheckout(planSlug, navigate, setLoading) {
  const token = getToken();
  if (!token) {
    // Not logged in — send to signup and replay checkout after account.
    navigate(`/signup?plan=${planSlug}`);
    return;
  }
  // Pre-open a blank tab SYNCHRONOUSLY during the click so we keep the
  // user-gesture context for the Stripe redirect (mobile Safari blocks
  // window.open() after an async fetch).
  const popup = openCheckoutSync();
  setLoading(planSlug);
  try {
    const backendKey = PLAN_ALIAS[planSlug] || planSlug;
    const origin = window.location.origin;
    const { data } = await api.post("/subscription/checkout", {
      plan_key: backendKey,
      success_url: `${origin}/billing?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${origin}/billing?checkout=cancelled`,
    });
    const checkoutUrl = data?.url || data?.checkout_url;
    if (!checkoutUrl) {
      throw new Error("Couldn't start checkout. Please try again.");
    }
    const ok = resolveCheckout(popup, checkoutUrl);
    if (!ok) {
      // Popup blocked AND parent nav failed — surface a visible link toast.
      toast.info("Tap here to open Stripe Checkout.", {
        action: { label: "Open", onClick: () => window.open(checkoutUrl, "_blank", "noopener,noreferrer") },
      });
    }
  } catch (e) {
    if (popup) { try { popup.close(); } catch { /* noop */ } }
    // If the backend says the session is expired / not authenticated,
    // fall back to the signup flow so the user can re-enter credentials
    // instead of getting stuck on the pricing page.
    const status = e?.response?.status;
    if (status === 401 || status === 403) {
      navigate(`/signup?plan=${planSlug}`);
      return;
    }
    toast.error(formatError(e, "Checkout failed. Please try again."));
  } finally {
    setLoading(null);
  }
}

const TIERS = [
  {
    slug: "diy",
    name: "DIY",
    units: "1 unit · get started",
    monthly_mo: 5,
    annual_mo: 5,
    tag: "Starter tier",
    bullets: [
      "Rent tracking for 1 unit",
      "Provincial lease generator",
      "Tenant portal access",
      "Basic maintenance log",
      "Email support",
      "Cancel anytime — no commitment",
    ],
  },
  {
    slug: "starter",
    name: "Starter",
    units: "up to 10 units",
    monthly_mo: 35,
    annual_mo: 30,
    tag: "Recommended",
    recommended: true,
    bullets: [
      "Everything in DIY, plus:",
      "Smart Mark assistant · 24/7 tenant chat",
      "All 13 provincial lease + notice generators",
      "CAD-native rent tracking (Stripe / Klarna / PAD)",
      "Tenant screening (credit + ID + reference)",
      "CRA T776 + NR4 exports",
      "Email + chat support",
    ],
  },
  {
    slug: "growth",
    name: "Pro / Growth",
    units: "11–50 units",
    monthly_mo: 50,
    annual_mo: 42,
    tag: "Most popular",
    featured: true,
    bullets: [
      "Everything in Starter, plus:",
      "Maintenance triage + vendor dispatch",
      "Bulk SMS rent reminders + late escalation",
      "Multi-property dashboards + portfolio KPIs",
      "Custom branded tenant portal",
      "Phone + chat support · 4-hour response SLA",
    ],
  },
  {
    slug: "portfolio",
    name: "Enterprise",
    units: "50+ units",
    monthly_mo: 80,
    annual_mo: 67,
    tag: "For scaling operators",
    bullets: [
      "Everything in Growth, plus:",
      "Dedicated onboarding manager",
      "API access + Webhooks",
      "White-label tenant portal (your domain)",
      "Owner statements + investor reports (T5013)",
      "Priority phone support · 1-hour SLA",
    ],
  },
];

function setMeta(sel, attrs) {
  let el = document.head.querySelector(sel);
  if (!el) { el = document.createElement(sel.startsWith("link") ? "link" : "meta"); document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

export default function Pricing() {
  const [annual, setAnnual] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const prev = document.title;
    document.title = "Pricing — Central Rentals · Canadian property management software";
    const desc = "Flat monthly Canadian property management: $60 / $150 / $250 CAD. Save 17% on annual prepay. $49 paid onboarding refunded against your first month.";
    const tags = [
      setMeta('meta[name="description"]', { name: "description", content: desc }),
      setMeta('link[rel="canonical"]', { rel: "canonical", href: "https://centralrentals.ca/pricing" }),
      setMeta('meta[property="og:title"]', { property: "og:title", content: "Central Rentals — Pricing" }),
      setMeta('meta[property="og:description"]', { property: "og:description", content: desc }),
    ];
    // Product/Offer JSON-LD — eligible for Google rich pricing cards.
    let s = document.head.querySelector('script[data-jsonld="pricing"]');
    if (!s) { s = document.createElement("script"); s.type = "application/ld+json"; s.setAttribute("data-jsonld", "pricing"); document.head.appendChild(s); }
    s.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "Product",
      "name": "Central Rentals — Canadian property management software",
      "brand": { "@type": "Brand", "name": "Central Rentals" },
      "offers": TIERS.map((t) => ({
        "@type": "Offer", "name": t.name, "priceCurrency": "CAD",
        "price": t.monthly_mo, "url": `https://centralrentals.ca/pricing#${t.slug}`,
        "description": `${t.units} · CA$${t.monthly_mo}/month`,
      })),
    });
    return () => { document.title = prev; tags.forEach((t) => t?.remove?.()); s?.remove?.(); };
  }, []);

  return (
    <div className="min-h-screen bg-white landing-page" data-testid="pricing-page">
      <PublicHeader light/>
      <EEATTrustBar testid="pricing-eeat" />
      <PublicBackButton to="/" label="Back to home"/>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-12 pb-6 text-center">
        <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-bold tracking-wide text-emerald-800">
          <MapPin className="w-3.5 h-3.5"/> Built for Canadian landlords · All 13 provinces
        </div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-slate-900">
          Simple, flat pricing for Canadian landlords
        </h1>
        <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto">
          Start at CA$35/month for 1–10 units. Scale to unlimited without ever calling sales. Every plan includes rent collection, provincial leases, and CRA-ready tax exports.
        </p>

        <div className="mt-6 flex justify-center">
          {/* LiveSignupCounter removed — fake social-proof widget replaced
              with real user reviews once they arrive. */}
          {false && <LiveSignupCounter />}
        </div>

        {/* Annual / Monthly toggle */}
        <div className="mt-8 inline-flex items-center gap-1.5 p-1 rounded-full border border-slate-200 bg-slate-50" data-testid="pricing-toggle">
          <button onClick={() => setAnnual(false)} data-testid="pricing-toggle-monthly"
            className={`px-4 py-1.5 text-sm font-bold rounded-full transition-colors ${!annual ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"}`}>
            Monthly
          </button>
          <button onClick={() => setAnnual(true)} data-testid="pricing-toggle-annual"
            className={`px-4 py-1.5 text-sm font-bold rounded-full transition-colors flex items-center gap-1.5 ${annual ? "bg-emerald-600 text-white shadow" : "text-slate-500 hover:text-slate-700"}`}>
            Annual <span className={`text-[10px] uppercase tracking-wider font-bold ${annual ? "bg-emerald-800/50 text-white" : "bg-emerald-100 text-emerald-700"} px-1.5 py-0.5 rounded`}>Save 17%</span>
          </button>
        </div>
      </section>

      {/* Tiers */}
      <section className="max-w-6xl mx-auto px-5 py-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="pricing-tiers">
        {TIERS.map((t) => {
          const shown = annual ? t.annual_mo : t.monthly_mo;
          const yrSaving = Math.round((t.monthly_mo - t.annual_mo) * 12);
          const isFeatured = t.featured || t.recommended;
          const triggerCheckout = () => {
            goToStripeCheckout(t.slug, navigate, setCheckoutLoading);
          };
          return (
            <div key={t.slug} id={t.slug} data-testid={`pricing-tier-${t.slug}`}
              role="button"
              tabIndex={0}
              onClick={triggerCheckout}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); triggerCheckout(); } }}
              className={`rounded-2xl p-6 relative cursor-pointer select-none focus:outline-none focus:ring-4 focus:ring-emerald-300/60 ${t.recommended ? "bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-2xl shadow-emerald-500/40 ring-4 ring-emerald-300/40 lg:scale-[1.03]" : t.featured ? "bg-gradient-to-br from-slate-800 to-slate-900 text-white shadow-2xl shadow-slate-900/30" : "bg-white border border-slate-200 shadow"}`}>
              {t.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white text-emerald-700 text-[10px] font-black uppercase tracking-widest shadow-md" data-testid="pricing-recommended-badge">
                  ★ Recommended
                </div>
              )}
              <div className={`text-[11px] uppercase tracking-widest font-bold ${isFeatured ? "text-emerald-100" : "text-emerald-700"}`}>{t.tag}</div>
              <div className={`mt-1 font-display text-2xl font-extrabold ${isFeatured ? "text-white" : "text-slate-900"}`}>{t.name}</div>
              <div className={`mt-1 text-xs ${isFeatured ? "text-emerald-100" : "text-slate-500"}`}>{t.units}</div>
              <div className="mt-5 flex items-baseline gap-1">
                <span className={`font-display text-5xl font-extrabold ${isFeatured ? "text-white" : "text-slate-900"}`}>${shown}</span>
                <span className={`text-sm ${isFeatured ? "text-emerald-100" : "text-slate-500"}`}>CAD / month</span>
              </div>
              {annual && yrSaving > 0 && (
                <div className={`mt-1 text-[11px] ${isFeatured ? "text-emerald-100" : "text-emerald-700"} font-semibold`}>
                  Billed annually · save ${yrSaving} CAD/yr vs monthly
                </div>
              )}
              <button
                type="button"
                disabled={checkoutLoading === t.slug}
                data-testid={`pricing-cta-${t.slug}`}
                onClick={(e) => { e.stopPropagation(); triggerCheckout(); }}
                className={`mt-6 w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl font-bold text-base transition-colors disabled:opacity-70 shadow-md ${isFeatured ? "bg-white text-emerald-700 hover:bg-emerald-50 border-2 border-white" : "bg-emerald-600 text-white hover:bg-emerald-700 border-2 border-emerald-600"}`}
              >
                {checkoutLoading === t.slug ? (
                  <><Loader2 className="w-4 h-4 animate-spin"/> Starting checkout…</>
                ) : (
                  <>Choose {t.name.split(" ")[0]} <ArrowRight className="w-4 h-4"/></>
                )}
              </button>
              <div className={`mt-1.5 text-center text-[11px] ${isFeatured ? "text-emerald-100" : "text-slate-500"}`} data-testid={`pricing-klarna-hint-${t.slug}`}>
                or split with Klarna · 4 × ${(Math.round(shown / 4 * 100) / 100).toFixed(2)}
              </div>
              <ul className={`mt-6 space-y-2.5 text-[14px] ${isFeatured ? "text-emerald-50" : "text-slate-700"}`}>
                {t.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className={`w-4 h-4 flex-none mt-0.5 ${isFeatured ? "text-white" : "text-emerald-600"}`}/>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>

      {/* Growth Calculator — drag units 1-100, see live cost & savings */}
      <GrowthCalculator annual={annual} />

      {/* Processing fees transparency — matches the Buildium/TenantCloud
          model. Central Rentals takes 0% of rent; you pay only Stripe's
          own processing rate, exactly like every other Canadian PMS. */}
      <section className="max-w-4xl mx-auto px-5 py-10" data-testid="pricing-processing-fees">
        <div className="rounded-2xl border border-slate-200 bg-white p-7 sm:p-9">
          <div className="flex items-start gap-4">
            <div className="flex-none w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center font-bold text-lg">$</div>
            <div className="flex-1">
              <div className="font-display text-2xl font-extrabold text-slate-900">Rent processing fees — no markup, ever</div>
              <p className="mt-2 text-slate-700 leading-relaxed">
                Your monthly subscription covers Central Rentals. Rent
                payments flow through Stripe at Stripe&apos;s standard rate — we
                add <b>zero</b> platform fee. Your tenant pays; your landlord
                account receives the rent minus Stripe&apos;s own processing.
              </p>
              <div className="mt-5 grid sm:grid-cols-3 gap-3">
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Credit / debit card</div>
                  <div className="mt-1 text-lg font-bold text-slate-900 font-mono">2.9% + $0.30</div>
                  <div className="text-xs text-slate-500 mt-1">Stripe standard · billed per charge</div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">ACH / e-Transfer</div>
                  <div className="mt-1 text-lg font-bold text-slate-900 font-mono">0.8% · $5 cap</div>
                  <div className="text-xs text-slate-500 mt-1">Cheapest option for large rents</div>
                </div>
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Central Rentals cut</div>
                  <div className="mt-1 text-lg font-bold text-emerald-900 font-mono">0%</div>
                  <div className="text-xs text-emerald-700 mt-1">Rent stays with the landlord</div>
                </div>
              </div>
              <p className="mt-4 text-xs text-slate-500">
                Comparable to Buildium / TenantCloud pricing (flat monthly + pass-through processing).
                Stripe rates confirmed at{" "}
                <a href="https://stripe.com/en-ca/pricing" target="_blank" rel="noopener noreferrer" className="underline text-emerald-700 hover:text-emerald-900">stripe.com/en-ca/pricing</a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Paid onboarding callout */}
      <section className="max-w-4xl mx-auto px-5 py-10">
        <div className="rounded-2xl bg-gradient-to-br from-slate-50 via-white to-emerald-50 border border-slate-200 p-7 sm:p-9" data-testid="pricing-onboarding">
          <div className="flex items-start gap-4">
            <div className="flex-none w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><Zap className="w-6 h-6"/></div>
            <div>
              <div className="font-display text-2xl font-extrabold text-slate-900">$49 paid onboarding · 100% refundable</div>
              <p className="mt-2 text-slate-700 leading-relaxed">
                A one-time CA$49 fit-check fee at signup. A senior team member spends 45 minutes with you, migrates your first 3 leases, and connects rent collection live on the call. <b>Refunded against your first month&apos;s bill if you stay past day-30.</b> This filters out tire-kickers and funds product development without VC dilution.
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <Link to="/signup" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-lg shadow-emerald-500/30" data-testid="pricing-onboarding-cta">
                  Book my fit-check call <ArrowRight className="w-4 h-4"/>
                </Link>
                <span className="text-xs text-slate-500">or split with Klarna · Refundable · Paid plan · No long-term contract</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Annual savings strip + trust */}
      <section className="max-w-6xl mx-auto px-5 py-12 border-t border-slate-100">
        <div className="grid md:grid-cols-4 gap-5 text-center">
          {[
            { icon: Sparkles, label: "17% off annual prepay", desc: "≈ 2 months free per year" },
            { icon: Shield, label: "Bank-grade Stripe", desc: "SOC2-aligned, CAD-native" },
            { icon: CheckCircle2, label: "Cancel anytime", desc: "No long-term contract" },
            { icon: Zap, label: "Klarna Pay-Later", desc: "Split your subscription over 4 payments" },
          ].map((p, i) => (
            <div key={i} className="rounded-xl bg-slate-50 border border-slate-200 p-5" data-testid={`pricing-trust-${i}`}>
              <p.icon className="w-6 h-6 text-emerald-600 mx-auto mb-2"/>
              <div className="font-display font-bold text-slate-900">{p.label}</div>
              <div className="text-xs text-slate-500 mt-1">{p.desc}</div>
            </div>
          ))}
        </div>
      </section>
      <SmartChatBubble />
    </div>
  );
}

/**
 * GrowthCalculator — drag-to-explore widget that makes the per-unit
 * pricing tangible. Shows live monthly cost, annual prepay savings,
 * and which tier the visitor falls into as they scale.
 */
function GrowthCalculator({ annual }) {
  const [units, setUnits] = useState(12);

  // Engagement-driven audience refinement — fires ONLY when the user
  // actually moves the slider (not on initial mount). Otherwise the
  // default units=12 would clobber an explicit utm_audience=pmc sticky.
  function onSlide(next) {
    setUnits(next);
    if (next >= 50) setAudience(AUDIENCES.PMC);
    else if (next <= 25) setAudience(AUDIENCES.INDIVIDUAL_LANDLORD);
  }

  const calc = useMemo(() => {
    const tier =
      units <= 10 ? TIERS[0] : units <= 50 ? TIERS[1] : TIERS[2];
    const monthlyCost = annual ? tier.annual_mo : tier.monthly_mo;
    const fullPriceMonthly = tier.monthly_mo;
    const fullPriceAnnual = fullPriceMonthly * 12;
    const discountedAnnual = monthlyCost * 12;
    const annualSavings = Math.max(0, +(fullPriceAnnual - discountedAnnual).toFixed(2));

    return {
      tier,
      monthlyCost,
      annualCost: discountedAnnual,
      annualSavings,
    };
  }, [units, annual]);

  return (
    <section
      data-testid="growth-calculator"
      className="max-w-5xl mx-auto px-5 pt-6 pb-2"
    >
      <div className="rounded-2xl border border-slate-200 bg-white shadow-lg overflow-hidden">
        <div className="px-6 sm:px-8 pt-7 pb-5 border-b border-slate-100 flex items-start gap-3">
          <div className="flex-none w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <Calculator className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display text-xl sm:text-2xl font-extrabold text-slate-900">
              Growth calculator — see your price as you scale
            </div>
            <p className="mt-1 text-sm text-slate-600">
              Drag the slider to see exactly what Central Rentals costs at your
              portfolio size. Per-unit price drops as you grow.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-0">
          {/* Slider side */}
          <div className="px-6 sm:px-8 py-7 border-b md:border-b-0 md:border-r border-slate-100">
            <label
              htmlFor="growth-slider"
              className="text-[11px] uppercase tracking-widest font-bold text-slate-500"
            >
              Units in portfolio
            </label>
            <div className="mt-2 flex items-baseline gap-2">
              <span
                data-testid="growth-units-value"
                className="font-display text-5xl sm:text-6xl font-extrabold text-slate-900 tabular-nums"
              >
                {units}
              </span>
              <span className="text-sm text-slate-500">
                {units === 1 ? "unit" : "units"}
                {units >= 100 ? "+" : ""}
              </span>
            </div>
            <input
              id="growth-slider"
              data-testid="growth-slider"
              type="range"
              min={1}
              max={100}
              step={1}
              value={units}
              onChange={(e) => onSlide(Number(e.target.value))}
              className="mt-5 w-full h-2 rounded-full appearance-none bg-slate-200 accent-emerald-600 cursor-pointer"
              aria-label="Number of units in your portfolio"
            />
            <div className="mt-2 flex justify-between text-[11px] font-semibold text-slate-400">
              <span>1</span>
              <span>25</span>
              <span>50</span>
              <span>75</span>
              <span>100+</span>
            </div>

            <div className="mt-6 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
              <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-800">
                Your tier
              </span>
              <span
                data-testid="growth-tier-badge"
                className="text-sm font-bold text-emerald-700"
              >
                {calc.tier.name} · ${calc.monthlyCost}/mo
              </span>
            </div>
          </div>

          {/* Results side */}
          <div className="px-6 sm:px-8 py-7 bg-gradient-to-br from-slate-50 via-white to-emerald-50/40">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">
                {annual ? "Effective monthly (billed annually)" : "Monthly cost"}
              </div>
              <div className="mt-1 flex items-baseline gap-1">
                <span
                  data-testid="growth-monthly-cost"
                  className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 tabular-nums"
                >
                  ${calc.monthlyCost.toFixed(2)}
                </span>
                <span className="text-sm text-slate-500">CAD / month</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                  Annual cost
                </div>
                <div
                  data-testid="growth-annual-cost"
                  className="mt-0.5 font-display text-lg font-extrabold text-slate-900 tabular-nums"
                >
                  ${calc.annualCost.toFixed(2)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">
                  You save (vs monthly)
                </div>
                <div
                  data-testid="growth-annual-savings"
                  className="mt-0.5 font-display text-lg font-extrabold text-emerald-700 tabular-nums"
                >
                  ${annual ? calc.annualSavings.toFixed(2) : "0.00"}/yr
                </div>
              </div>
            </div>

            <Link
              to="/signup"
              data-testid="growth-calculator-cta"
              onClick={() => { import("@/lib/google-tag").then((g) => g.fireGetStarted({ source: "growth_calculator" })); }}
              className="mt-6 w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-md shadow-emerald-500/30 transition-colors"
            >
              Choose a plan <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-2 text-[11px] text-slate-500 text-center">
              Billed monthly · Cancel anytime
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
